import Box from '@mui/material/Box';
import { SxProps } from '@mui/material/styles';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import {
    ICertificateValidationStatus,
    IContentTypeValue,
    IPortfolioItemChildFragment,
} from 'graphql/types';
import { TableCell } from 'common/components/Table';
import { PortfolioItemProgressScoreChip } from 'user/components/PortfolioItemProgressScoreChip';
import { formatDate } from 'common/utils/formatDate';
import { receivedCertificateEndDateNear } from 'common/utils/receivedCertificate';

const tableRowStyle: SxProps = {
    cursor: 'pointer',
    '&:focus-visible': {
        outlineOffset: '-2px',
    },
};

interface IProps {
    hasMandatoryItems: boolean;
    item: IPortfolioItemChildFragment;
    onClick?(): void;
}

export const ReceivedCertificatePortfolioItemTableRow = ({
    hasMandatoryItems,
    item,
    onClick,
    ...other
}: IProps) => {
    const [translate] = useTranslation();

    const { endDate } = item;

    const isExpired = !!(!!endDate && new Date(endDate) < new Date());
    const isEndDateNear = !!endDate && receivedCertificateEndDateNear(endDate);

    const endDateDisplay = item.endDate
        ? formatDate(item.endDate, 'dd MMMM yyyy')
        : null;

    const isReceivedCertificate =
        item.relatedType === IContentTypeValue.ReceivedCertificate;

    const { title, metadata } = item;
    const { certificateName, validationStatus: certificateValidationStatus } =
        metadata || {};

    const itemTitle = isReceivedCertificate ? certificateName : title;

    const validationStatus = certificateValidationStatus ?? undefined;

    const isMandatory = !!metadata?.isMandatory;
    const isNotAchieved =
        validationStatus === ICertificateValidationStatus.Denied ||
        validationStatus === ICertificateValidationStatus.Pending ||
        (!validationStatus && (!endDate || isExpired));
    const actionRequired = translate(
        'receivedCertificateStatus.actionRequired'
    );

    let baseStatus: string | null = null;

    if (!isMandatory && hasMandatoryItems) {
        baseStatus = translate('optional');
    } else if (isMandatory && isNotAchieved) {
        baseStatus = actionRequired;
    }

    const { statusText, statusColor } = (() => {
        if (isExpired && endDateDisplay) {
            if (baseStatus) {
                return {
                    statusText: (
                        <>
                            <Box component="span" sx={{ color: 'error.main' }}>
                                {endDateDisplay}
                            </Box>
                            {`, ${baseStatus}`}
                        </>
                    ),
                    statusColor: 'text.secondary' as const,
                };
            }

            return {
                statusText: endDateDisplay,
                statusColor: 'error.main' as const,
            };
        }

        if (isEndDateNear && endDateDisplay && baseStatus) {
            return {
                statusText: `${translate('receivedCertificateExpiryNear', {
                    date: endDateDisplay,
                })}, ${baseStatus}`,
                statusColor: 'text.secondary' as const,
            };
        }

        return {
            statusText: baseStatus,
            statusColor: 'text.secondary' as const,
        };
    })();

    return (
        <TableRow
            hover
            sx={tableRowStyle}
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter') onClick?.();
            }}
            {...other}
        >
            <TableCell sx={{ width: 0 }}>
                <PortfolioItemProgressScoreChip
                    withIcon
                    isMandatory={isMandatory}
                    portfolioItem={item}
                    receivedCertificateValidationStatus={validationStatus}
                    size="large"
                />
            </TableCell>

            <TableCell>{itemTitle}</TableCell>

            <TableCell align="right">
                {!!isEndDateNear && !isExpired && !baseStatus && (
                    <Typography color="text.secondary" variant="body2">
                        {translate('receivedCertificateExpiryNear', {
                            date: endDateDisplay,
                        })}
                    </Typography>
                )}

                {statusText && (
                    <Typography color={statusColor} variant="body2">
                        {statusText}
                    </Typography>
                )}
            </TableCell>
        </TableRow>
    );
};
